import { useCallback, useEffect, useRef, useState } from 'react';
import { interBankService, InterSyncStatus } from '@/services/interBankService';

interface UseInterBankSyncPollerOptions {
  enabled?: boolean;
  intervalMs?: number;
}

export function useInterBankSyncPoller(options: UseInterBankSyncPollerOptions = {}) {
  const { enabled = true, intervalMs = 15000 } = options;
  const [syncStatus, setSyncStatus] = useState<InterSyncStatus | null>(null);
  const [syncVersion, setSyncVersion] = useState(0);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const versionRef = useRef(0);
  const initializedRef = useRef(false);

  const poll = useCallback(async () => {
    try {
      const sync = await interBankService.getSyncStatus();
      setSyncStatus(sync);

      if (!initializedRef.current) {
        initializedRef.current = true;
        versionRef.current = sync.syncVersion;
        setSyncVersion(sync.syncVersion);
        return sync;
      }

      if (sync.syncVersion !== versionRef.current) {
        versionRef.current = sync.syncVersion;
        setSyncVersion(sync.syncVersion);
        setRefreshCounter((current) => current + 1);
      }

      return sync;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    poll();
    const interval = setInterval(poll, intervalMs);
    return () => clearInterval(interval);
  }, [enabled, intervalMs, poll]);

  return { syncStatus, syncVersion, refreshCounter, poll };
}
